import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';

import { Product } from '@ttstr/api/products';
import {
  Container,
  NativeSlider,
  VariantChooser,
  useShopConfig,
  ContentrArea,
  useProductDetailLink,
} from '@ttstr/components';

interface OwnProps {
  readonly product: Product;
}

type Props = Readonly<OwnProps>;

const ProductDetail: React.FC<Props> = ({ product }) => {
  const { t } = useTranslation();
  const { canonicalUrl } = useShopConfig();
  const productDetailLink = useProductDetailLink();

  const images = React.useMemo(() => [product.hero_image.url, ...product.gallery_image_urls.map((i) => i.original)], [
    product,
  ]);
  const thumbs = React.useMemo(
    () => [product.hero_image.thumb.url, ...product.gallery_image_urls.map((i) => i.thumb)],
    [product]
  );

  return (
    <article className="product-detail">
      <Helmet>
        <body className="product-detail-page inverse-navbar" />
      </Helmet>

      <section className="product-content">
        <Container className="detail-page">
          <Row>
            <Col lg="7">
              <NativeSlider
                images={images.length ? images : [product.hero_image.url]}
                thumbs={thumbs.length ? thumbs : [product.hero_image.thumb.url]}
              />
            </Col>
            <Col lg="5" className="text-center text-lg-left">
              <header className="mt-4 mt-lg-0">
                {product.supertitle && <span className="text-muted">{product.supertitle}</span>}
                <h1 className="product-detail-title">
                  <span>{product.title}</span>
                  {product.subtitle && <small>{product.subtitle}</small>}
                </h1>
              </header>
              <ContentrArea id="before-variants" />
              <VariantChooser className="mb-4" product={product} showFormerPrice />
              <ContentrArea id="after-variants" />
              {product.description && (
                <>
                  <h5 className="text-left">{t(`PRODUCT.INFORMATION`)}</h5>
                  <div className="text-left" dangerouslySetInnerHTML={{ __html: product.description }} />
                </>
              )}
              <ContentrArea id="general-info" />
              <small className="d-block text-muted mt-2">{t(`PRODUCT.EXCLVAT`)}</small>
            </Col>
          </Row>
        </Container>
      </section>
    </article>
  );
};

export default React.memo(ProductDetail);
